import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Wrench, AlertTriangle, CheckCircle2, Loader2, FileCheck2 } from 'lucide-react';
import { ProductionImpactDisclosure } from './ProductionImpactDisclosure';
import { playSuccessChime, playFailureChime } from '../utils/maintenanceAudio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

/**
 * v6.32.11 — One-click MIME-mismatch cleanup for production.
 *
 * Context: MOSAIC image engine has always returned JPEG bytes, but historical
 * generations saved them with a .png extension. Nginx/Cloudflare then served
 * those files as Content-Type: image/png on actual JFIF bytes — a mismatch
 * that strict browsers + Cloudflare's strict-MIME refuse to render (broken
 * image icons). v6.32.9 fixed the pipeline for NEW images; this panel lets a
 * Super Admin run the one-time cleanup on the current DB+filesystem:
 *
 *   1. Sniff magic bytes in /bp_images/, /cultural_images/, /bp_review/.
 *   2. Rename every JPEG-under-.png file to .jpg (in-place filesystem rename;
 *      zero disk cost).
 *   3. Bulk-update every DB URL reference in questions + manifests + logs.
 *   4. Optionally auto-trigger the 113-BLM press-PDF regen so the printed
 *      booklets re-embed bytes against the new paths.
 *
 * Dry-run shows the plan without changes.
 */
const BPMimeFixPanel = () => {
  const [dryResult, setDryResult] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [regenBlms, setRegenBlms] = useState(true);
  // v6.32.233 — live status from background worker (v6.32.232 made the
  // endpoint async-detached so we poll /mime-fix-status here instead of
  // blocking on the request → no more Cloudflare 520 toast on Production).
  const [liveStatus, setLiveStatus] = useState(null);
  const [runId, setRunId] = useState(null);

  // Poll the status endpoint while a run is active. Stops cleanly when
  // status hits complete | failed | cancelled or when the run_id resets.
  React.useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${API}/bp-auditor/mime-fix-status`,
          { ...authCfg(), params: { run_id: runId }, timeout: 10000 },
        );
        if (cancelled) return;
        setLiveStatus(data);
        if (data?.status === 'complete' || data?.status === 'failed') {
          setExecuting(false);
          setResult(data);
          if (data.status === 'complete') {
            const dbUpdates = data.db_updates || {};
            playSuccessChime();  // v6.34.4
            toast.success(
              `Renamed ${data.files_renamed ?? 0} files, updated ` +
              `${Object.values(dbUpdates).reduce((a, b) => a + b, 0)} DB refs.`
            );
            if (data.press_regen_triggered) {
              setTimeout(() => toast.info(
                'BLM regeneration started in background (~3-5 min). ' +
                'Check the Press PDF status chip for progress.'
              ), 1500);
            }
          } else {
            playFailureChime();  // v6.34.4
            toast.error(`Cleanup failed: ${data.failure_reason || 'unknown error'}`);
          }
          setRunId(null);  // stop polling
        }
      } catch (e) {
        // Transient poll errors are NOT a failure — keep trying. Only
        // surface to the user if we've been polling for 60s without a
        // successful response.
      }
    };
    tick();  // immediate
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [runId]);

  const doDryRun = async () => {
    // Password isn't required for dry-run semantically, but the endpoint
    // still checks auth. We ask for the password once up front so both
    // dry-run and execute share the same gate.
    if (!pw) { toast.error('Enter your Super Admin password to preview'); return; }
    setDryResult(null);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/mime-fix-and-refresh`,
        { password: pw, regen_blms: regenBlms, dry_run: true },
        { ...authCfg(), timeout: 60000 },
      );
      setDryResult(data);
      if ((data.files_found || 0) === 0) {
        toast.success('No mismatched files found — your image library is already clean.');
      } else {
        toast.info(`Preview: ${data.files_found} files would be renamed, ${Object.values(data.db_would_update || {}).reduce((a, b) => a + b, 0)} DB refs would be updated.`);
      }
    } catch (e) {
      toast.error(`Dry run failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const doExecute = async () => {
    if (!pw) { toast.error('Password required'); return; }
    setExecuting(true);
    setResult(null);
    setLiveStatus(null);
    try {
      // v6.32.232 backend now returns 202-style {accepted, run_id, ...}
      // immediately. Polling effect above handles the rest.
      const { data } = await axios.post(
        `${API}/bp-auditor/mime-fix-and-refresh`,
        { password: pw, regen_blms: regenBlms, dry_run: false },
        { ...authCfg(), timeout: 30000 },
      );
      setConfirmOpen(false);
      if (data.accepted && data.run_id) {
        setRunId(data.run_id);
        toast.info(`Cleanup started for ${data.files_planned} file${data.files_planned === 1 ? '' : 's'} — polling for progress…`);
      } else {
        // v6.32.231-and-earlier fallback: the response was synchronous
        // and already contains files_renamed + db_updates.
        setExecuting(false);
        setResult(data);
        toast.success(`Renamed ${data.files_renamed} files, updated ${Object.values(data.db_updates || {}).reduce((a, b) => a + b, 0)} DB refs.`);
      }
    } catch (e) {
      setExecuting(false);
      toast.error(`Execute failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const totalDry = dryResult?.files_found || 0;
  const totalExec = result?.files_renamed || 0;

  return (
    <div className="bg-white border-2 border-orange-300 rounded-xl p-5" data-testid="bp-mime-fix-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-orange-100 text-orange-700 flex items-center justify-center rounded-lg flex-shrink-0">
          <Wrench size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-lg font-bold text-gray-900">MIME-Mismatch Cleanup (v6.32.11)</h3>
            <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-[10px] font-semibold border border-orange-300">
              prod fix · one-time
            </span>
          </div>
          <p className="text-sm text-gray-700 max-w-3xl">
            Scans <code className="text-xs bg-gray-100 px-1">/bp_images/</code>, <code className="text-xs bg-gray-100 px-1">/cultural_images/</code>, and <code className="text-xs bg-gray-100 px-1">/bp_review/</code> for files saved as <code className="text-xs bg-gray-100 px-1">.png</code> whose actual bytes are JPEG — the root cause of broken image icons in the Refine Big Picture dialog and anywhere else strict browsers enforce MIME. Renames every mismatched file to <code className="text-xs bg-gray-100 px-1">.jpg</code>, updates every MongoDB URL reference, and (optionally) auto-regenerates the 113 printed BLM booklets so they re-embed against the corrected paths.
          </p>

          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Super Admin password</Label>
              <Input
                type="password"
                value={pw}
                onChange={e => setPw(e.target.value)}
                placeholder="Required to run migration"
                className="mt-1"
                data-testid="bp-mime-fix-password"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={regenBlms}
                  onChange={e => setRegenBlms(e.target.checked)}
                  data-testid="bp-mime-fix-regen-blms"
                />
                <span className="text-gray-700">Auto-regenerate 113 BLM booklets after migration (recommended)</span>
              </label>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={doDryRun}
              disabled={!pw}
              data-testid="bp-mime-fix-dry-run"
            >
              <FileCheck2 size={14} className="mr-1" />
              Preview (dry-run)
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!pw || executing}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="bp-mime-fix-execute"
            >
              {executing
                ? <><Loader2 size={14} className="mr-1 animate-spin" />Running…</>
                : <><Wrench size={14} className="mr-1" />Run cleanup now</>}
            </Button>
          </div>

          {/* Dry-run summary */}
          {dryResult && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="bp-mime-fix-dry-result">
              <div className="font-semibold text-blue-900 mb-1">
                Dry-run: {totalDry} mismatched file{totalDry === 1 ? '' : 's'} found
              </div>
              {totalDry > 0 && dryResult.per_dir && (
                <ul className="text-xs text-blue-800 space-y-0.5 list-disc ml-5">
                  {Object.entries(dryResult.per_dir).map(([dir, n]) => n > 0 && (
                    <li key={dir}><code>{dir.replace('/app/frontend/public', '')}</code>: <strong>{n}</strong></li>
                  ))}
                </ul>
              )}
              {totalDry > 0 && dryResult.db_would_update && (
                <div className="text-xs text-blue-800 mt-2">
                  DB updates that would be applied:{' '}
                  {Object.entries(dryResult.db_would_update)
                    .filter(([, n]) => n > 0)
                    .map(([k, n]) => `${k.split('.').pop()}=${n}`)
                    .join(' · ') || 'none'}
                </div>
              )}
              {totalDry === 0 && (
                <div className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> Library is already MIME-correct. Nothing to do.</div>
              )}
            </div>
          )}

          {/* v6.32.233 — live progress strip while the detached worker
              runs (visible only between confirm-click and complete/failed). */}
          {liveStatus && executing && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm"
                 data-testid="bp-mime-fix-live-progress">
              <div className="font-semibold text-amber-900 flex items-center gap-1.5 mb-1">
                <Loader2 size={14} className="animate-spin" />
                Cleanup in progress · {liveStatus.phase || liveStatus.status}
              </div>
              <ul className="text-xs text-amber-800 space-y-0.5 list-disc ml-5">
                <li><strong>{liveStatus.files_renamed ?? 0}</strong> / {liveStatus.files_planned ?? '?'} files renamed</li>
                {liveStatus.db_updates && (
                  <li>
                    DB updates so far:{' '}
                    {Object.entries(liveStatus.db_updates)
                      .filter(([, n]) => n > 0)
                      .map(([k, n]) => `${k.split('.').pop()}=${n}`)
                      .join(' · ') || '0'}
                  </li>
                )}
                <li className="text-amber-700">Run ID: <code>{liveStatus.run_id?.slice(0, 8)}…</code></li>
              </ul>
            </div>
          )}

          {/* Execute result */}
          {result && (
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-300 rounded-lg text-sm" data-testid="bp-mime-fix-result">
              <div className="font-semibold text-emerald-900 flex items-center gap-1.5 mb-1">
                <CheckCircle2 size={16} /> Cleanup complete
              </div>
              <ul className="text-xs text-emerald-800 space-y-0.5 list-disc ml-5">
                <li><strong>{totalExec}</strong> files renamed to <code>.jpg</code></li>
                {result.db_updates && Object.entries(result.db_updates).filter(([, n]) => n > 0).map(([k, n]) => (
                  <li key={k}><code>{k}</code>: {n} updates</li>
                ))}
                {result.press_regen_triggered && (
                  <li className="text-emerald-900 font-semibold">BLM regeneration started (runs in background ~3-5 min)</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-orange-600" />
              Run MIME-mismatch cleanup?
            </DialogTitle>
            <DialogDescription>
              This will rename every <code>.png</code> file with JPEG bytes to <code>.jpg</code> and update every MongoDB reference. Rollback manifests are updated so historical rollbacks still work.
              {regenBlms && <> After the rename completes, the 113 BLM booklets will be regenerated automatically (runs in background; ~3-5 min).</>}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            <strong>Safe to run</strong> — file renames use <code>shutil.move</code> (inode-preserving filesystem rename, no copy/delete race, zero disk cost). Already-migrated files are skipped. Idempotent.
          </div>
          <ProductionImpactDisclosure
            testId="bp-mime-fix-disclosure"
            removes={[
              <>Renames every <code className="bg-gray-50 px-1">.png</code> file with JPEG bytes (JFIF magic) to <code className="bg-gray-50 px-1">.jpg</code> across <code className="bg-gray-50 px-1">/bp_images/</code>, <code className="bg-gray-50 px-1">/cultural_images/</code>, <code className="bg-gray-50 px-1">/bp_review/</code></>,
              <>Updates 6 MongoDB URL fields: <code className="bg-gray-50 px-1">questions.bp_image_url</code>, <code className="bg-gray-50 px-1">bp_audit_manifests.new_bp_image_url</code> + <code className="bg-gray-50 px-1">prev_bp_image_url</code> + <code className="bg-gray-50 px-1">candidate_url</code>, <code className="bg-gray-50 px-1">bp_audit_log.candidate_url</code>, <code className="bg-gray-50 px-1">audit_logs.bp_image_url</code></>,
              regenBlms ? <>After rename completes, regenerates all 113 BLM print booklet PDFs in background (~3-5 min) so they re-embed against corrected paths</> : <>BLM regen <strong>skipped</strong> — uncheck above to enable</>,
              <>One <code className="bg-gray-50 px-1">audit_logs</code> row recording the operation (file count + DB update breakdown)</>,
            ]}
            doesNotTouch={[
              <>Image bytes themselves — only filename + extension change. The actual JPEG content is preserved byte-for-byte (filesystem inode preserved via <code className="bg-gray-50 px-1">shutil.move</code>)</>,
              <>Marketing collateral PDFs (sell sheet, procurement letters, kit spec sheet)</>,
              <>Question text, answers, rubrics — only image URL pointers change</>,
              <>Student data, exam state, in-progress assessments, sessions</>,
              <>Already-migrated files (skipped automatically — idempotent)</>,
            ]}
            note="Rollback available: every URL update is recorded in bp_audit_manifests for reversal if needed."
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={executing} data-testid="bp-mime-fix-cancel">
              Cancel
            </Button>
            <Button
              onClick={doExecute}
              disabled={executing}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="bp-mime-fix-confirm"
            >
              {executing
                ? <><Loader2 size={14} className="mr-1 animate-spin" />Running…</>
                : 'Run cleanup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BPMimeFixPanel;
